import {
  AvatarGroup, Box, Typography, useTheme,
} from '@mui/material';
import {
  DataGridPro, GridApiPro, GridCellParams, GridColDef, useGridApiRef, GridActionsCellItemProps,
} from '@mui/x-data-grid-pro';
import startCase from 'lodash/startCase';
import SvgIcon from '@mui/material/SvgIcon';
import {
  MutableRefObject, useCallback, useRef, useState, ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import LinearProgress from '@mui/material/LinearProgress';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { MemoizedBaseAvatar, BaseTooltip, Label } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { TableActions } from '../../../components/table/TableActions';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { useTeamPermissions } from '../../team/hooks';
import { usePermissions } from '../../../hooks/useCommon';
import { getAuthenticateUser } from '../../../store/authenticate/selectors';
import { ReactComponent as ViewIcon } from '../../../static/icons/ic_view.svg';
import { PrintWrapper } from '../../../layout/wrapper/PrintWrapper';
import { useTable } from '../../../hooks/useTable';
import { useGetTeamsDataAPI } from '../../../hooks/api/useCommissions';
import { getIsPrintMode } from '../../../store/common/selectors';
import { TeamTableHeader } from '../../team/components/Table/TeamTableHeader';
import { commissionsActions } from '../../../store/commissions/slice';
import { getCommissionTeams } from '../../../store/commissions/selectors';
import { TCommissionPlan, TMember } from '../../../types';

const TeamCommissionsTable = () => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const { t } = useTranslation(['inputs', 'team', 'common', 'commissions']);
  const dispatch = useAppDispatch();
  const { isFetching } = useGetTeamsDataAPI(true);
  const { teamPermissions } = useTeamPermissions();
  const { permissions } = usePermissions();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const { localText, handleScrollTable, handleExport } = useTable(apiRef);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const { pageHeight, resizableDiv, isDesktopSmallScreen } = useScreenResize(59);
  const teamCommissions = useAppSelector(getCommissionTeams);

  const [isFullTable, setIsFullTable] = useState(false);

  const teamsColumns: Array<GridColDef> = [
    {
      field: 'teamName',
      headerName: t('teamName', { ns: 'commissions' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <MemoizedBaseAvatar avatarFile={row.avatarFile} avatarName={row.teamName} />
          <Typography variant="caption">
            {startCase(row.teamName)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'members',
      headerName: t('members', { ns: 'team' }),
      minWidth: 250,
      renderCell: ({ row }) => (
        <Box px={1} display="flex" alignItems="center" height="100%">
          <AvatarGroup
            total={row.members.length}
            sx={{
              '.MuiAvatarGroup-avatar:first-of-type': {
                width: isMobile ? 30 : 35,
                height: isMobile ? 30 : 35,
              },
            }}
          >
            {row.members.map((member: TMember) => (
              <BaseTooltip key={member.email} tooltipText={member.name}>
                <MemoizedBaseAvatar
                  sx={{
                    marginRight: '0',
                  }}
                  size={{
                    width: isMobile ? 30 : 35,
                    height: isMobile ? 30 : 35,
                  }}
                  avatarName={member.name}
                  avatarFile={member.avatarFile}
                />
              </BaseTooltip>
            ))}
          </AvatarGroup>
        </Box>
      ),
      valueGetter: (value: Array<TMember>) => {
        if (Array.isArray(value)) {
          const memberNames = value?.map((member) => member.name);

          return memberNames?.join(' ,');
        }

        return value;
      },
    },
    {
      field: 'commissionPlan',
      headerName: t('commissionPlan', { ns: 'orders' }),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.commissionPlan?.name || '/'}
          </Typography>
        </Box>
      ),
      valueGetter: (value: TCommissionPlan) => value?.name || '',
    },
    {
      field: 'commissionModal',
      headerName: t('commissionModel', { ns: 'commissions' }),
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Label
            variant="soft"
            color={
              (row.commissionModel?.compensation === 'split' && 'primary')
              || (row.commissionModel?.compensation === 'duplicate' && 'info')
              || (row.commissionModel?.compensation === 'team steps' && 'success')
              || 'error'
            }
          >
            {t(row.commissionModel?.compensation)}
          </Label>
        </Box>
      ),
      valueGetter: (_, row) => t(row.commissionModel?.compensation),
    },
    {
      field: 'calculateBy',
      headerName: t('goalsBasedOn', { ns: 'commissions' }),
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          {row.commissionModel.goalsBase ? (
            <Label
              variant="soft"
              color={row.commissionModel.goalsBase === 'location' ? 'secondary' : 'warning'}
            >
              {t(`${row.commissionModel.goalsBase}`)}
            </Label>
          )
            : <Typography variant="caption">/</Typography>}
        </Box>
      ),
      valueGetter: (_, row) => t(`${row.commissionModel.goalsBase}`),
    },
    {
      field: 'location',
      headerName: t('location'),
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.commissionModel?.baseLocation?.name || '/'}
          </Typography>
        </Box>
      ),
      valueGetter: (_, row) => row.commissionModel?.baseLocation?.name,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions', { ns: 'common' }),
      disableExport: true,
      sortable: false,
      align: 'right',
      getActions: ({ row }) => {
        const openDeleteDialog = () => {
          dispatch(commissionsActions.setIsOpenDeleteTeamDialog(true));
          dispatch(commissionsActions.setSelectedTeam(row));
        };

        const openTeamNav = () => {
          dispatch(commissionsActions.setSelectedTeam(row));

          setTimeout(() => {
            dispatch(commissionsActions.setIsOpenTeamCommissionsNav(true));
          }, 100);
        };

        const dynamicIcon = (
          (!teamPermissions.isAllowManage || permissions.isEmployee)
          && row.email === personnel.email ? (
            <SvgIcon onClick={openTeamNav} component={ViewIcon} fontSize="small" />
            ) : null
        );

        return TableActions({
          onConfirm: teamPermissions.isAllowManage && !permissions.isEmployee ? openTeamNav : undefined,
          onDelete: teamPermissions.isAllowManage && !permissions.isEmployee
            ? openDeleteDialog : undefined,
          dynamicIcon,
        }) as ReactElement<GridActionsCellItemProps>[];
      },
    },
  ];

  const onCellDoubleClick = (params: GridCellParams) => {
    dispatch(commissionsActions.setSelectedTeam(params.row));

    setTimeout(() => {
      dispatch(commissionsActions.setIsOpenTeamCommissionsNav(true));
    }, 100);
  };

  const renderTableHeader = useCallback(() => (
    <TeamTableHeader
      handleScrollTable={handleScrollTable}
      printRef={componentRef}
      apiRef={apiRef}
      isFullTable={isFullTable}
      setIsFullTable={setIsFullTable}
      handleExport={() => handleExport('mgm-team-commissions')}
    />
  ), [apiRef, isFullTable]);

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData', { ns: 'common' })} />;

  return (
    <PrintWrapper componentRef={componentRef}>
      <div ref={resizableDiv}>
        <Card
          sx={{
            // eslint-disable-next-line no-nested-ternary
            height: isDesktopSmallScreen ? 'auto' : isFullTable && teamCommissions?.length > 0 ? 'auto' : pageHeight !== 0 && !isPrintMode ? pageHeight : 'auto',
            pb: 6,
            ...(isMobile && {
              height: 'auto',
              background: 'unset',
              boxShadow: 'unset',
              px: 0,
              pt: 0,
              pb: '30px',
            }),
          }}
        >
          <DataGridPro
            apiRef={apiRef}
            rows={teamCommissions}
            localeText={localText}
            slots={{
              toolbar: renderTableHeader as any,
              loadingOverlay: LinearProgress as any,
              noRowsOverlay: renderCustomNoRowsOverlay,
            }}
            loading={isFetching}
            sx={{
              px: 0,
              '.MuiDataGrid-main > *:first-of-type': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                printOptions: {
                  hideFooter: true,
                  hideToolbar: true,
                },
              },
            }}
            {...(!isMobile && {
              initialState: { pinnedColumns: { left: ['name'], right: ['actions'] } },
            })}
            getRowId={(row) => row.id}
            columns={teamsColumns}
            hideFooterPagination
            disableRowSelectionOnClick
            hideFooter
            showToolbar
            onCellDoubleClick={onCellDoubleClick}
            checkboxSelection={false}
            className={isPrintMode ? 'print-mode' : ''}
          />
        </Card>
      </div>
    </PrintWrapper>

  );
};

export { TeamCommissionsTable };
